/**
 * Tamanho do SMS conforme o envio do MailerSend e o GSM 03.38:
 * https://developers.mailersend.com/api/v1/sms
 *
 * GSM-7 cabe em 160 septetos. Na mensagem concatenada, o cabeçalho
 * (UDH, 6 bytes) deixa 153 por parte. A tabela estendida ocupa 2.
 * Unicode (UCS-2 / UTF-16) cabe em 70 e, concatenado, em 67.
 * Um emoji fora do plano básico ocupa 2 unidades UTF-16.
 * O texto da API aceita no máximo 2048 caracteres.
 */

export const SMS_API_MAX_CHARS = 2048;
export const SMS_GSM7_SINGLE = 160;
export const SMS_GSM7_CONCAT = 153;
export const SMS_UCS2_SINGLE = 70;
export const SMS_UCS2_CONCAT = 67;

const GSM_BASIC_CHARS =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#$%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà¤';
const GSM_EXTENDED_CHARS = '\f^{}\\[~]|€';

const GSM_BASIC = new Set(GSM_BASIC_CHARS);
const GSM_EXTENDED = new Set(GSM_EXTENDED_CHARS);

export type SmsEncoding = 'gsm-7' | 'ucs-2';

export interface SmsTextMeasure {
  /** Pontos de código. O teto de 2048 da API usa esta conta. */
  chars: number;
  /** Septetos GSM-7 ou unidades UTF-16. É o que define as partes. */
  units: number;
  encoding: SmsEncoding;
  singlePartLimit: typeof SMS_GSM7_SINGLE | typeof SMS_UCS2_SINGLE;
  multipartLimit: typeof SMS_GSM7_CONCAT | typeof SMS_UCS2_CONCAT;
  /** 0 se vazio. Senão, partes já com o cabeçalho de concatenação. */
  parts: number;
  /** Unidades ocupadas na parte em curso. */
  segmentUnits: number;
  /** Capacidade da parte em curso: 160/70 ou 153/67. */
  segmentLimit: number;
  concatenated: boolean;
  exceedsApiMax: boolean;
  /** `{` e `}` fora de `{{variavel}}` são rejeitados no `text`. */
  strayBraces: boolean;
  /** Símbolos da tabela estendida. Zero quando a mensagem é Unicode. */
  extendedChars: number;
  /** Primeiro caractere fora do GSM-7. */
  nonGsmSample: string | null;
}

export function hasStrayBraces(text: string): boolean {
  const withoutTokens = text.replace(/\{\{[A-Za-z0-9_]+\}\}/g, '');
  return withoutTokens.includes('{') || withoutTokens.includes('}');
}

function segmentProgress(units: number, single: number, multi: number) {
  if (units <= 0) return { parts: 0, segmentUnits: 0, segmentLimit: single };
  if (units <= single) return { parts: 1, segmentUnits: units, segmentLimit: single };
  const parts = Math.ceil(units / multi);
  const remainder = units % multi;
  return { parts, segmentUnits: remainder === 0 ? multi : remainder, segmentLimit: multi };
}

export function measureSmsText(text: string): SmsTextMeasure {
  const chars = [...text].length;
  let septets = 0;
  let extendedChars = 0;
  let nonGsmSample: string | null = null;

  for (const char of text) {
    if (GSM_BASIC.has(char)) {
      septets += 1;
    } else if (GSM_EXTENDED.has(char)) {
      septets += 2;
      extendedChars += 1;
    } else {
      nonGsmSample = char;
      break;
    }
  }

  const encoding: SmsEncoding = nonGsmSample == null ? 'gsm-7' : 'ucs-2';
  const units = encoding === 'gsm-7' ? septets : text.length;
  const singlePartLimit = encoding === 'gsm-7' ? SMS_GSM7_SINGLE : SMS_UCS2_SINGLE;
  const multipartLimit = encoding === 'gsm-7' ? SMS_GSM7_CONCAT : SMS_UCS2_CONCAT;
  const progress = segmentProgress(units, singlePartLimit, multipartLimit);

  return {
    chars,
    units,
    encoding,
    singlePartLimit,
    multipartLimit,
    parts: progress.parts,
    segmentUnits: progress.segmentUnits,
    segmentLimit: progress.segmentLimit,
    concatenated: progress.parts > 1,
    exceedsApiMax: chars > SMS_API_MAX_CHARS,
    strayBraces: hasStrayBraces(text),
    extendedChars: encoding === 'gsm-7' ? extendedChars : 0,
    nonGsmSample,
  };
}

export function smsEncodingLabel(encoding: SmsEncoding): string {
  return encoding === 'gsm-7' ? 'GSM-7' : 'Unicode (UCS-2)';
}
